/**
 * Dashboard History Chart
 *
 * Multi-line chart showing historical daily performance. Series colours are
 * drawn from the shared `metricColors` token map so each line matches its
 * corresponding card icon + left-edge accent on the QA Reports page.
 *
 * All series are toggle-able via clickable legend.
 * Follows DefectTrendChart pattern with production floor readability.
 * Uses larger fonts (14px) and thicker lines (3px) for distance viewing.
 */

import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO, isValid } from 'date-fns';
import { TrendingUp } from 'lucide-react';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { METRIC_COLORS } from '@/features/qa-reports/metricColors';
import type { DashboardHistoryPoint } from '../types';

type SeriesKey = 'ordersShipped' | 'garmentsProcessed' | 'garmentsPrinted' | 'batchesCompleted';

const SERIES_ORDER: SeriesKey[] = ['ordersShipped', 'garmentsProcessed', 'garmentsPrinted', 'batchesCompleted'];

// Chart series colours mirror the QA Reports cards so a given metric wears
// the same colour wherever it appears. See apps/frontend/src/features/qa-reports/metricColors.ts
const chartConfig: ChartConfig = {
  ordersShipped: {
    label: 'Orders Shipped',
    color: `hsl(${METRIC_COLORS.ordersShipped.hsl})`,
  },
  garmentsProcessed: {
    label: 'Items Processed',
    color: `hsl(${METRIC_COLORS.itemsProcessed.hsl})`,
  },
  garmentsPrinted: {
    label: 'Items Printed',
    color: `hsl(${METRIC_COLORS.itemsPrinted.hsl})`,
  },
  batchesCompleted: {
    label: 'Batches Completed',
    color: `hsl(${METRIC_COLORS.batchesCompleted.hsl})`,
  },
};

interface DashboardHistoryChartProps {
  data: DashboardHistoryPoint[];
  isLoading?: boolean;
  /** Date range for empty state context */
  dateRangeLabel?: string;
}

/**
 * Safely parse and format a date string.
 * Returns fallback text if parsing fails (defensive programming).
 */
function safeFormatDate(date: string, formatStr: string, fallback = '-'): string {
  if (!date) return fallback;
  try {
    const parsed = parseISO(date);
    if (!isValid(parsed)) return fallback;
    return format(parsed, formatStr);
  } catch {
    return fallback;
  }
}

/**
 * Hook to detect reduced motion preference.
 * Respects user accessibility settings for animations.
 */
function useReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function DashboardHistoryChart({
  data,
  isLoading,
  dateRangeLabel,
}: DashboardHistoryChartProps) {
  const { t } = useTranslation('production');
  const prefersReducedMotion = useReducedMotion();
  const [hiddenSeries, setHiddenSeries] = useState<Set<SeriesKey>>(new Set());

  // i18n-aware labels for legend + tooltip. The base chartConfig retains
  // the static English labels so it can live at module scope; here we
  // overlay translated values so the legend and tooltip show the operator's
  // language without duplicating the colour tokens.
  const seriesLabels: Record<SeriesKey, string> = useMemo(() => ({
    ordersShipped: t('dashboard.history.ordersShipped'),
    garmentsProcessed: t('dashboard.history.itemsProcessed'),
    garmentsPrinted: t('dashboard.history.itemsPrinted'),
    batchesCompleted: t('dashboard.history.batchesCompleted'),
  }), [t]);

  const translatedConfig: ChartConfig = useMemo(() => ({
    ordersShipped: { ...chartConfig.ordersShipped, label: seriesLabels.ordersShipped },
    garmentsProcessed: { ...chartConfig.garmentsProcessed, label: seriesLabels.garmentsProcessed },
    garmentsPrinted: { ...chartConfig.garmentsPrinted, label: seriesLabels.garmentsPrinted },
    batchesCompleted: { ...chartConfig.batchesCompleted, label: seriesLabels.batchesCompleted },
  }), [seriesLabels]);

  // Animation duration - respect reduced motion preference
  const animationDuration = prefersReducedMotion ? 0 : 300;

  const toggleSeries = useCallback((key: SeriesKey) => {
    setHiddenSeries(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // Don't allow hiding all series
        if (next.size >= SERIES_ORDER.length - 1) return prev;
        next.add(key);
      }
      return next;
    });
  }, []);

  // Generate accessible description from data
  const chartDescription = useMemo(() => {
    if (!data || data.length === 0) return 'No data available';
    const firstDate = safeFormatDate(data[0]?.date ?? '', 'MMM d');
    const lastDate = safeFormatDate(data[data.length - 1]?.date ?? '', 'MMM d');
    const totalOrders = data.reduce((sum, d) => sum + d.ordersShipped, 0);
    const totalGarments = data.reduce((sum, d) => sum + d.garmentsProcessed, 0);
    const totalPrinted = data.reduce((sum, d) => sum + (d.garmentsPrinted ?? 0), 0);
    const totalBatches = data.reduce((sum, d) => sum + d.batchesCompleted, 0);
    return `Historical performance from ${firstDate} to ${lastDate}. Total: ${totalOrders} orders shipped, ${totalGarments} items processed, ${totalPrinted} items printed, ${totalBatches} batches completed.`;
  }, [data]);

  // Identify series that have any non-zero point in the range. An all-zero
  // series draws a misleading flat line that looks like activity — we mark
  // them as "no data" in the legend so the reader can tell the difference.
  const seriesHasData = useMemo(() => {
    const totals: Record<SeriesKey, number> = {
      ordersShipped: 0,
      garmentsProcessed: 0,
      garmentsPrinted: 0,
      batchesCompleted: 0,
    };
    for (const point of data ?? []) {
      totals.ordersShipped += point.ordersShipped ?? 0;
      totals.garmentsProcessed += point.garmentsProcessed ?? 0;
      totals.garmentsPrinted += point.garmentsPrinted ?? 0;
      totals.batchesCompleted += point.batchesCompleted ?? 0;
    }
    return totals;
  }, [data]);

  // Format x-axis labels as short date (e.g., "Jan 15")
  const formatXAxis = (date: string) => safeFormatDate(date, 'MMM d');

  // Format tooltip date as full date (e.g., "Monday, Jan 15, 2026")
  const formatTooltipDate = (date: string) => safeFormatDate(date, 'EEEE, MMM d, yyyy');

  // Loading state with enhanced skeleton that hints at chart structure
  if (isLoading) {
    return (
      <div className="h-80 w-full relative">
        <Skeleton className="h-full w-full rounded-lg" />
        {/* Grid lines skeleton to hint at chart structure */}
        <div className="absolute inset-0 flex flex-col justify-between py-10 px-12 pointer-events-none">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-px w-full opacity-30" />
          ))}
        </div>
      </div>
    );
  }

  // Empty state with date context
  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-80 text-center">
        <div className="p-3 rounded-full bg-muted mb-4">
          <TrendingUp className="h-8 w-8 text-muted-foreground" />
        </div>
        <p className="text-lg font-medium">{t('dashboard.history.noDataYet')}</p>
        <p className="text-muted-foreground max-w-sm mt-1">
          {dateRangeLabel
            ? t('dashboard.history.noDataForRange', { range: dateRangeLabel })
            : t('dashboard.history.noDataDesc')}
        </p>
      </div>
    );
  }

  return (
    <div role="img" aria-label={chartDescription}>
      {/* Screen reader summary */}
      <div className="sr-only">{chartDescription}</div>

      {/* Clickable legend — coloured dot pairs each series with its card. */}
      <div className="flex flex-wrap gap-2 mb-5 justify-center">
        {SERIES_ORDER.map((key) => {
          const config = chartConfig[key]!;
          const isHidden = hiddenSeries.has(key);
          const hasData = seriesHasData[key] > 0;
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggleSeries(key)}
              className={cn(
                'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                'border border-transparent transition-colors',
                'hover:border-border hover:bg-muted/40',
                (isHidden || !hasData) && 'opacity-40',
              )}
              title={!hasData ? t('dashboard.history.seriesNoData') : undefined}
            >
              <span
                className="h-2.5 w-2.5 rounded-full shrink-0 ring-2 ring-background"
                style={{ backgroundColor: config.color as string }}
              />
              <span className={cn('tracking-tight', isHidden && 'line-through decoration-1')}>
                {seriesLabels[key]}
                {!hasData && (
                  <span className="ml-1 text-muted-foreground">· n/a</span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <ChartContainer config={translatedConfig} className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              className="stroke-muted"
              strokeOpacity={0.45}
            />
            <XAxis
              dataKey="date"
              tickFormatter={formatXAxis}
              tick={{ fontSize: 12, fontVariant: 'tabular-nums' }}
              tickMargin={8}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 12, fontVariant: 'tabular-nums' }}
              tickMargin={8}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              width={36}
            />
            <ChartTooltip
              cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeOpacity: 0.3, strokeDasharray: '3 3' }}
              content={
                <ChartTooltipContent
                  labelFormatter={(label) => formatTooltipDate(label)}
                />
              }
            />
            {SERIES_ORDER.map((key) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={`var(--color-${key})`}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 5, strokeWidth: 2, stroke: 'hsl(var(--background))' }}
                animationDuration={animationDuration}
                animationEasing="ease-out"
                hide={hiddenSeries.has(key) || seriesHasData[key] === 0}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
}
